// -- SVG: Top-down studio layout diagrams -----------------------------------

export function StudioDiagram({ variant, legend, title, dark = false }) {
  // variant: "small" or "mid"
  const W = 520;
  const H = 360;
  const stroke = dark ? "#3F3F46" : "#9CA3AF";
  const wallFill = dark ? "#18181B" : "#F9FAFB";
  const text = dark ? "#E4E4E7" : "#111827";
  const muted = dark ? "#A1A1AA" : "#4B5563";

  // Positions tuned per variant
  const layout =
    variant === "small"
      ? {
          bg: { x: 60, y: 70, w: 400, h: 40 },
          subject: { x: 260, y: 170 },
          key: { x: 150, y: 170 },
          fill: { x: 370, y: 170 },
          rim: { x: 260, y: 100 },
          bgLight: null,
          camera: { x: 260, y: 290 },
          roomLabel: "10 x 12 ft",
        }
      : {
          bg: { x: 50, y: 60, w: 420, h: 40 },
          subject: { x: 260, y: 180 },
          key: { x: 130, y: 180 },
          fill: { x: 390, y: 180 },
          rim: { x: 260, y: 100 },
          bgLight: { x: 120, y: 100 },
          bgLight2: { x: 400, y: 100 },
          camera: { x: 260, y: 310 },
          roomLabel: "15 x 20 ft",
        };

  return (
    <div className="w-full">
      <h4 className="text-sm font-semibold mb-2" style={{ color: text }}>
        {title}
      </h4>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={title}>
        {/* Room */}
        <rect
          x="20"
          y="20"
          width={W - 40}
          height={H - 40}
          rx="6"
          fill={wallFill}
          stroke={stroke}
          strokeWidth="2"
        />
        <text x={W - 30} y="38" textAnchor="end" fontSize="11" fill={muted}>
          {layout.roomLabel}
        </text>

        {/* Background */}
        <rect
          x={layout.bg.x}
          y={layout.bg.y}
          width={layout.bg.w}
          height={layout.bg.h}
          fill="#71717A"
          stroke={stroke}
        />
        <text x={layout.bg.x + layout.bg.w / 2} y={layout.bg.y - 6} textAnchor="middle" fontSize="10" fill={muted}>
          {legend.bg}
        </text>

        {/* Dotted light paths */}
        <line x1={layout.key.x} y1={layout.key.y} x2={layout.subject.x} y2={layout.subject.y} stroke="#F5A524" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7" />
        <line x1={layout.fill.x} y1={layout.fill.y} x2={layout.subject.x} y2={layout.subject.y} stroke="#60A5FA" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7" />
        <line x1={layout.rim.x} y1={layout.rim.y} x2={layout.subject.x} y2={layout.subject.y} stroke="#A78BFA" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.7" />

        {/* Subject */}
        <circle cx={layout.subject.x} cy={layout.subject.y} r="14" fill="#F5A524" stroke={stroke} strokeWidth="2" />
        <text x={layout.subject.x} y={layout.subject.y + 4} textAnchor="middle" fontSize="10" fontWeight="bold" fill="#0A0A0A">
          S
        </text>
        <text x={layout.subject.x} y={layout.subject.y + 32} textAnchor="middle" fontSize="10" fill={text}>
          {legend.subject}
        </text>

        {/* Key light */}
        <polygon points={`${layout.key.x - 10},${layout.key.y - 10} ${layout.key.x + 10},${layout.key.y} ${layout.key.x - 10},${layout.key.y + 10}`} fill="#F5A524" stroke={stroke} />
        <text x={layout.key.x} y={layout.key.y - 16} textAnchor="middle" fontSize="10" fill={text}>
          {legend.key}
        </text>

        {/* Fill light */}
        <polygon points={`${layout.fill.x + 10},${layout.fill.y - 10} ${layout.fill.x - 10},${layout.fill.y} ${layout.fill.x + 10},${layout.fill.y + 10}`} fill="#60A5FA" stroke={stroke} />
        <text x={layout.fill.x} y={layout.fill.y - 16} textAnchor="middle" fontSize="10" fill={text}>
          {legend.fill}
        </text>

        {/* Rim light (behind subject, pointing forward) */}
        <polygon points={`${layout.rim.x - 8},${layout.rim.y + 10} ${layout.rim.x + 8},${layout.rim.y + 10} ${layout.rim.x},${layout.rim.y - 4}`} fill="#A78BFA" stroke={stroke} />
        <text x={layout.rim.x} y={layout.rim.y - 10} textAnchor="middle" fontSize="10" fill={text}>
          {legend.rim}
        </text>

        {/* Background lights (mid only) */}
        {layout.bgLight && (
          <>
            <circle cx={layout.bgLight.x} cy={layout.bgLight.y} r="7" fill="#34D399" stroke={stroke} />
            <circle cx={layout.bgLight2.x} cy={layout.bgLight2.y} r="7" fill="#34D399" stroke={stroke} />
            <text x={layout.bgLight.x} y={layout.bgLight.y + 22} textAnchor="middle" fontSize="9" fill={text}>
              {legend.bg_light}
            </text>
            <text x={layout.bgLight2.x} y={layout.bgLight2.y + 22} textAnchor="middle" fontSize="9" fill={text}>
              {legend.bg_light}
            </text>
          </>
        )}

        {/* Camera */}
        <rect x={layout.camera.x - 16} y={layout.camera.y - 11} width="32" height="22" rx="3" fill={dark ? "#27272A" : "#E5E7EB"} stroke={stroke} strokeWidth="2" />
        <circle cx={layout.camera.x} cy={layout.camera.y} r="6" fill={dark ? "#52525B" : "#6B7280"} stroke={stroke} />
        <text x={layout.camera.x} y={layout.camera.y + 28} textAnchor="middle" fontSize="10" fill={text}>
          {legend.camera}
        </text>
      </svg>
    </div>
  );
}
